/**
 * The two things a `<dialog>` on this site has to do that the platform does not always do.
 *
 * Both of these were written for the incident drawer (F17) and are lifted here unchanged because
 * the share sheet is the second modal on the page and would otherwise carry a second copy of
 * them. The reasoning is the drawer's and still holds:
 *
 *   - **`showModal` may not exist.** jsdom implements no dialog behaviour at all, and a browser
 *     can refuse a second `showModal` on the same element. Falling back to the `open` attribute
 *     keeps the panel rendered — but an `open` dialog without `showModal` is a floating div with
 *     no inertness and no trap, which is worse than no modal. That is what `trapTab` is for.
 *   - **A trap that only works when the platform feels like it is not a trap.** Every browser
 *     that ships `showModal` already wraps Tab, so in production this listener never fires. It is
 *     there for the case where the first bullet took the fallback path.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'

/** Open or close the element to match `open`, tolerating an environment with neither method. */
export function syncDialogOpen(dialog: HTMLDialogElement, open: boolean): void {
  if (open) {
    if (dialog.open) return
    try {
      dialog.showModal()
    } catch {
      dialog.setAttribute('open', '')
    }
    // The panel rather than the first control: a screen reader then reads what this dialog is
    // about before it reads "close", which is the order a person needs them in.
    dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus()
    return
  }
  if (!dialog.open) return
  // Paired with the fallback above — an environment with no `showModal` has no `close` either,
  // and calling it would throw on the way out of a dialog that opened fine.
  if (typeof dialog.close === 'function') dialog.close()
  else dialog.removeAttribute('open')
}

/**
 * Wrap Tab inside the dialog. Call from `onKeyDown` on the `<dialog>` itself.
 *
 * Returns nothing; it either lets the event through or prevents it and moves focus.
 */
export function trapTab(event: {
  key: string
  shiftKey: boolean
  currentTarget: HTMLDialogElement
  preventDefault(): void
}): void {
  if (event.key !== 'Tab') return
  const dialog = event.currentTarget
  const focusable = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (node) => !node.hasAttribute('disabled') && node.tabIndex !== -1,
  )
  if (focusable.length === 0) return
  const first = focusable[0]!
  const last = focusable[focusable.length - 1]!
  const active = dialog.ownerDocument.activeElement as HTMLElement | null
  if (!event.shiftKey) {
    if (active !== last) return
    event.preventDefault()
    first.focus()
    return
  }
  // Backwards from the first control — or from the panel itself, which is where focus starts —
  // wraps to the end rather than escaping into the page behind the modal.
  if (active !== null && active !== first && focusable.includes(active)) return
  event.preventDefault()
  last.focus()
}
